import React, { useState, useMemo, useRef } from "react";
import { UseFormRegister, FieldError, UseFormTrigger } from "react-hook-form";
import {
  ExclamationCircleIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: FieldError;
  register?: UseFormRegister<any>;
  name?: string;
  icon?: React.ReactNode;
  containerClassName?: string;
  type?: string;
  trigger?: UseFormTrigger<any>;
  allowedChars?: "numeric" | "letters" | "alphanumeric";
  value?: string; // For controlled inputs
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void; // For controlled inputs
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void; // For controlled inputs
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  register,
  name,
  icon,
  className = "",
  containerClassName = "",
  type = "text",
  trigger,
  onChange: propsOnChange,
  onBlur: propsOnBlur,
  allowedChars,
  onKeyDown: propsOnKeyDown,
  onPaste: propsOnPaste,
  autoComplete = "off", // Add this line
  ...props
}, forwardedRef) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [fileName, setFileName] = useState("");

  const inputClasses = `
    block w-full rounded-lg border-2
    bg-white px-4 py-2 mb-2 text-gray-900
    placeholder-gray-400
    transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-offset-2
    disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed
    shadow-sm hover:shadow-md
    ${
      error
        ? "border-danger-default focus:border-danger-default focus:ring-danger-default"
        : "border-gray-200 focus:border-primary-default focus:ring-primary-default"
    }
    ${className}
  `;

  const togglePasswordVisibility = () => {
    setIsPasswordVisible((prev) => !prev);
  };

  // Check if this is a controlled input (has value prop) or uncontrolled (uses register)
  const isControlled = props.value !== undefined;
  
  // Get register props and combine onChange handlers (only for uncontrolled inputs)
  // For file inputs, we need to store the original onChange to call it manually
  const originalRegisterOnChangeRef = useRef<((e: React.ChangeEvent<HTMLInputElement>) => void) | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  
  const registerProps = useMemo(() => {
    if (isControlled || !register || !name) {
      return { onChange: undefined, onBlur: undefined, ref: undefined, name: undefined };
    }

    const registered = register(name);
    // For file inputs, store the original onChange but exclude it from registerProps
    // to prevent React Hook Form from interfering with the native file input display
    if (type === "file") {
      originalRegisterOnChangeRef.current = registered.onChange;
      // Store the register ref separately for file inputs
      if (registered.ref) {
        if (typeof registered.ref === "function") {
          // Store the function ref
          fileInputRef.current = null;
          // We'll call this in the combined ref handler
        } else {
          fileInputRef.current = (registered.ref as React.MutableRefObject<HTMLInputElement | null>).current;
        }
      }
      // Return only onBlur and name - exclude onChange and ref (we'll handle ref manually)
      return { 
        onBlur: registered.onBlur, 
        name: registered.name,
        onChange: undefined,
        ref: undefined
      };
    }
    return registered;
  }, [isControlled, register, name, type]);

  // Handle blur event - for controlled inputs, call custom onBlur; for uncontrolled, use register's onBlur
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (type === "file") {
      // For file inputs, only call custom onBlur — never call register's onBlur.
      // Clicking a file input opens a native dialog which fires blur immediately
      // before any file is selected. If register's onBlur runs, RHF validates
      // with mode:"onBlur" and shows premature "File is required" errors.
      // File validation should only happen on form submit.
      propsOnBlur?.(e);
    } else if (isControlled) {
      // For controlled inputs, call the custom onBlur handler
      propsOnBlur?.(e);
    } else if (registerProps.onBlur) {
      // For uncontrolled inputs, call register's onBlur
      registerProps.onBlur(e);
    } else {
      // Fallback to props onBlur if provided
      propsOnBlur?.(e);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Update the fileName state for display
    if (e.target.files?.length) {
      setFileName(e.target.files[0].name);
    } else {
      // Clear fileName if no file is selected
      setFileName("");
    }
    // Call custom onChange first (for setValue) to update React Hook Form's state
    if (propsOnChange) {
      propsOnChange(e);
    }
    // Then call register's onChange to ensure React Hook Form tracks the file input
    // Use requestAnimationFrame to ensure the file input's native state is preserved
    if (originalRegisterOnChangeRef.current) {
      requestAnimationFrame(() => {
        if (originalRegisterOnChangeRef.current) {
          originalRegisterOnChangeRef.current(e);
        }
      });
    }
  };

  // Handle key down to prevent invalid characters
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow special keys (backspace, delete, tab, escape, enter, arrow keys, etc.)
    const specialKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      "Clear",
      "Copy",
      "Paste",
      "Cut",
    ];

    // Allow Ctrl/Cmd + A, C, V, X, Z (select all, copy, paste, cut, undo)
    if (e.ctrlKey || e.metaKey) {
      if (["a", "c", "v", "x", "z"].includes(e.key.toLowerCase())) {
        return;
      }
    }

    // If it's a special key, allow it
    if (specialKeys.includes(e.key)) {
      propsOnKeyDown?.(e);
      return;
    }

    // Check if this is a phone number field (numeric + name is phoneNumber)
    const isPhoneNumber = allowedChars === "numeric" && name === "phoneNumber";
    const target = e.target as HTMLInputElement;
    const currentValue = target.value || "";

    // For phone number fields, prevent typing if already 10 digits
    if (isPhoneNumber && currentValue.length >= 10 && /^[0-9]$/.test(e.key)) {
      e.preventDefault();
      return;
    }

    // Check allowed characters based on allowedChars prop
    if (allowedChars === "numeric") {
      // Only allow numbers (0-9)
      if (!/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        return;
      }
    } else if (allowedChars === "letters") {
      // Only allow letters (a-z, A-Z) and space
      if (!/^[a-zA-Z\s]$/.test(e.key)) {
        e.preventDefault();
        return;
      }
    } else if (allowedChars === "alphanumeric") {
      // Allow letters and numbers
      if (!/^[a-zA-Z0-9]$/.test(e.key)) {
        e.preventDefault();
        return;
      }
    }

    // Call custom onKeyDown handler if provided
    propsOnKeyDown?.(e);
  };

  // Handle paste to filter invalid characters
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!allowedChars) {
      propsOnPaste?.(e);
      return;
    }

    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");

    let filteredText = "";
    if (allowedChars === "numeric") {
      // Only keep numbers
      filteredText = pastedText.replace(/\D/g, "");
    } else if (allowedChars === "letters") {
      // Only keep letters and spaces
      filteredText = pastedText.replace(/[^a-zA-Z\s]/g, "");
    } else if (allowedChars === "alphanumeric") {
      // Keep letters and numbers
      filteredText = pastedText.replace(/[^a-zA-Z0-9]/g, "");
    }

    // Check if this is a phone number field
    const isPhoneNumber = allowedChars === "numeric" && name === "phoneNumber";
    const target = e.target as HTMLInputElement;
    const start = target.selectionStart || 0;
    const end = target.selectionEnd || 0;
    const currentValue = target.value || "";

    // For phone number fields, limit to 10 digits total
    if (isPhoneNumber) {
      const remainingSpace = 10 - (currentValue.length - (end - start));
      if (remainingSpace <= 0) {
        // Already at max length, don't paste anything
        propsOnPaste?.(e);
        return;
      }
      // Trim filtered text to fit within 10 digit limit
      filteredText = filteredText.substring(0, remainingSpace);
    }

    // Insert the filtered text at the cursor position
    const newValue =
      currentValue.substring(0, start) +
      filteredText +
      currentValue.substring(end);

    // Update the input value
    target.value = newValue;

    // Set cursor position after the pasted text
    const newCursorPosition = start + filteredText.length;
    setTimeout(() => {
      target.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);

    // Trigger onChange event manually
    const syntheticEvent = {
      ...e,
      target: { ...target, value: newValue },
      currentTarget: { ...target, value: newValue },
    } as React.ChangeEvent<HTMLInputElement>;

    if (register && name && registerProps.onChange) {
      registerProps.onChange(syntheticEvent);
    } else {
      propsOnChange?.(syntheticEvent);
    }

    // Call custom onPaste handler if provided
    propsOnPaste?.(e);
  };

  return (
    <div className={`space-y-1.5 ${containerClassName} animate-fade-in`}>
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-gray-700"
        >
          {label}
        </label>
      )}
      <div className="relative">
        {icon && type !== "file" && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
            {icon}
          </div>
        )}
        <input
          id={name}
          type={
            type === "password"
              ? isPasswordVisible
                ? "text"
                : "password"
              : type
          }
          className={`
            ${inputClasses}
            ${icon && type !== "file" ? "pl-10" : ""}
            ${type === "file" ? "p-1" : ""}
          `}
          autoComplete={autoComplete} // Add this line
          maxLength={
            allowedChars === "numeric" && name === "phoneNumber"
              ? 10
              : props.maxLength
          }
          ref={(element) => {
            // Handle forwarded ref (when not using register)
            if (forwardedRef) {
              if (typeof forwardedRef === "function") {
                forwardedRef(element);
              } else {
                (forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current = element;
              }
            }
            // Also handle register ref when register is provided (for file inputs)
            if (!isControlled && register && name && type === "file") {
              const registered = register(name);
              if (registered.ref) {
                if (typeof registered.ref === "function") {
                  registered.ref(element);
                } else {
                  (registered.ref as React.MutableRefObject<HTMLInputElement | null>).current = element;
                }
              }
            }
            // Store in fileInputRef for file inputs
            if (type === "file") {
              fileInputRef.current = element;
            }
          }}
          {...(isControlled ? {} : (forwardedRef && type === "file" && !register ? {} : (() => {
            // Exclude ref from registerProps to avoid conflict with our ref handler
            const { ref, ...rest } = registerProps as any;
            return rest;
          })()))}
          {...props}
          value={isControlled ? props.value : type === "file" ? undefined : undefined}
          defaultValue={isControlled ? undefined : type === "file" ? undefined : props.defaultValue}
          onChange={
            type === "file"
              ? handleFileChange
              : isControlled
              ? (e: React.ChangeEvent<HTMLInputElement>) => {
                  // Check if this is a phone number field and limit to 10 digits
                  const isPhoneNumber =
                    allowedChars === "numeric" && name === "phoneNumber";
                  if (isPhoneNumber && e.target.value.length > 10) {
                    e.target.value = e.target.value.substring(0, 10);
                  }
                  propsOnChange?.(e);
                }
              : register && name && registerProps.onChange
              ? registerProps.onChange
              : propsOnChange
          }
          onBlur={handleBlur}
          onKeyDown={type !== "file" ? handleKeyDown : propsOnKeyDown}
          onPaste={type !== "file" ? handlePaste : propsOnPaste}
        />
        {/* Password toggle */}
        {type === "password" && (
          <div
            onClick={togglePasswordVisibility}
            className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
          >
            {isPasswordVisible ? (
              <EyeSlashIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <EyeIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>
        )}
        {/* File preview */}
        {type === "file" && fileName && (
          <p className="mt-1 text-sm text-gray-600 truncate">{fileName}</p>
        )}
        {/* Error icon */}
        {error && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <ExclamationCircleIcon
              className="h-5 w-5 text-danger-default"
              aria-hidden="true"
            />
          </div>
        )}
      </div>
      {/* Error text */}
      {error && (
        <p className="mt-1 text-sm text-danger-default animate-fade-in">
          {error.message}
        </p>
      )}
    </div>
  );
});

Input.displayName = "Input";

export default Input;
